import {
  Handle,
  Position,
  useNodeConnections,
  useNodesData,
  getOutgoers,
  useReactFlow,
  useEdges,
} from "@xyflow/react";
import { useEffect, useState, useMemo } from "react";
import {
  ArchiveBoxIcon,
  BoltIcon,
  ChatBubbleBottomCenterIcon,
  CheckBadgeIcon,
  ClockIcon,
  Cog6ToothIcon,
  PlusIcon,
  TrashIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { PhoneIcon } from "@heroicons/react/16/solid";
import { useWorkflowBuilder } from "../index2";
import { message, Popover, Tooltip } from "antd";
import { Badge } from "V2.0/common/badge";
import { FaWhatsapp } from "react-icons/fa";
import { EndNodeType } from "../models/endNode";
import { TbWebhook } from "react-icons/tb";

const Icons = {
  delay: () => <ClockIcon className="size-4 text-gray-500" />,
  communication: (communicationType) => CommunicationIcons[communicationType],
  analysis: () => <ClockIcon className="size-4 text-gray-500" />,
  trigger: () => <BoltIcon className="size-4 text-gray-500" />,
  assistant: () => <WrenchScrewdriverIcon className="size-4 text-gray-500" />,
  end: (endNodeType) => EndNodeIcons[endNodeType],
};

const CommunicationIcons = {
  whatsapp: <FaWhatsapp className="size-4 text-gray-500" />,
  call: <PhoneIcon className="size-4 text-gray-500" />,
  sms: <ChatBubbleBottomCenterIcon className="size-4 text-gray-500" />,
};

const EndNodeIcons = {
  [EndNodeType.Default]: <CheckBadgeIcon className="size-4 text-gray-500" />,
  [EndNodeType.ArchiveConversation]: (
    <ArchiveBoxIcon className="size-4 text-gray-500" />
  ),
  [EndNodeType.WebhookNotification]: (
    <TbWebhook className="size-4 text-gray-500" />
  ),
};

export const CustomNode = ({ id }) => {
  const internalNode = useNodesData(id);
  const edges = useEdges();
  const { openPanel, isPanelOpen } = useWorkflowBuilder();
  const { getNode, getNodeConnections, getNodes, getEdges } = useReactFlow();
  const node = getNode(id);
  const { data } = internalNode;
  const [open, setOpen] = useState(false);

  function shouldShowAddStep() {
    const nodes = getNodes();

    const outgoingEdges = getOutgoers(node, nodes, edges);

    if (data?.nodeType === "assistant" && outgoingEdges.length === 1) {
      return false;
    }

    if (data?.nodeType === "trigger" && outgoingEdges.length === 1) {
      return false;
    }

    if (data?.nodeType === "end") {
      return false;
    }

    if (data?.nodeType === "analysis") {
      const { parameter } = data?.settings;
      if (parameter.parameterChoices.length === outgoingEdges.length) {
        return false;
      } else {
        return true;
      }
    }

    return true;
  }

  const showAddStep = shouldShowAddStep();

  return (
    <>
      {data?.nodeType !== "trigger" && (
        <Handle type="target" position={Position.Top} />
      )}
      <div
        className={`cursor-pointer w-[300px] h-[90px] bg-white p-4 rounded-lg shadow-md border
            border-2 "border-blue-500 flex flex-col justify-between`}
      >
        <div className="flex justify-between items-center">
          <Tooltip title={data?.settings?.name}>
            <div className="flex gap-x-2 items-center">
              {Icons[data?.nodeType](data?.settings?.endNodeType)}
              <span className="text-sm font-medium max-w-[150px] truncate">
                {data?.settings?.name ||
                  (data?.nodeType === "trigger"
                    ? "Inbound Trigger"
                    : "unnamed action")}
              </span>
            </div>
          </Tooltip>

          <div className="flex space-x-2">
            {showAddStep && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (node.data.nodeType === "trigger") {
                    openPanel({
                      mode: "add",
                      data: {
                        ...node,
                        output: "status",
                        value: "completed",
                      },
                    });
                    return;
                  }

                  if (node.data.nodeType === "assistant") {
                    if (node.data.settings.monitoringSettings.length === 0) {
                      openPanel({
                        mode: "Configure",
                        data: node,
                        options: { triggerSave: true },
                      });
                      return;
                    }

                    if (node.data.settings.monitoringSettings.length === 1) {
                      const monitoringSettings =
                        node.data.settings.monitoringSettings;
                      const { statusToSet } = monitoringSettings[0];

                      openPanel({
                        mode: "add",
                        data: {
                          ...node,
                          output: "status",
                          value: statusToSet,
                        },
                      });

                      return;
                    }
                  }

                  if (node.data.nodeType === "analysis") {
                    if (
                      node.data.settings.analysisType === "" ||
                      (node.data.settings.analysisType === "param_extraction" &&
                        node.data.settings.parameter === "") ||
                      node.data.settings.outputVariableName === "" ||
                      node.data.settings.textSource === ""
                    ) {
                      message.error(
                        "Resolve the errors before adding next step"
                      );
                      openPanel({
                        mode: "Configure",
                        data: node,
                        options: { triggerSave: true },
                      });
                      return;
                    }
                  }

                  openPanel({ mode: "addPath", data: node });
                  // onDelete(id);
                }}
                disabled={isPanelOpen}
                className={`bg-none border-none cursor-pointer text-lg hover:bg-gray-200 rounded p-1 disabled:cursor-not-allowed`}
              >
                <Tooltip title="Add Step">
                  <PlusIcon className="size-4 text-gray-600" />
                </Tooltip>
              </button>
            )}

            {data.nodeType != "trigger" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  data.onDelete();
                }}
                disabled={isPanelOpen}
                className={`bg-none border-none cursor-pointer text-lg hover:bg-gray-200 rounded p-1 disabled:cursor-not-allowed`}
              >
                <Tooltip title="Delete">
                  <TrashIcon className="size-4 text-gray-600" />
                </Tooltip>
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                openPanel({ mode: "Configure", data: node });
              }}
              className={`bg-none border-none cursor-pointer text-lg hover:bg-gray-200 rounded p-1 disabled:cursor-not-allowed`}
            >
              <Tooltip title="Configure">
                <Cog6ToothIcon className="size-4 text-gray-600" />
              </Tooltip>
            </button>
          </div>
        </div>
        {data?.settings?.description && (
          <Tooltip title={data?.settings?.description} placement="bottom">
            <p className="text-xs text-gray-500 truncate">
              {data?.settings?.description}
            </p>
          </Tooltip>
        )}
      </div>
      {data?.nodeType !== "end" && (
        <Handle type="source" position={Position.Bottom} />
      )}
    </>
  );
};
